import { z } from "zod";

export const id = () => crypto.randomUUID();
const text = z.string().max(10000);
const key = z.string().min(1).max(200);
const iso = z.iso.datetime();
export const modes = [
  "understand",
  "cloze",
  "produce",
  "listen",
  "collocation",
] as const;
export type Mode = (typeof modes)[number];
export const modeLabels: Record<Mode, string> = {
  understand: "Cümleyi anlama",
  cloze: "Boşluk doldurma",
  produce: "Türkçeden İngilizceye",
  listen: "Dinleme",
  collocation: "Kalıp testi",
};
export const directions = ["receptive", "productive", "collocation"] as const;
export type Direction = (typeof directions)[number];
export const wordSchema = z.object({
  id: key,
  lemma: text.min(1).max(150),
  sense: text.min(1),
  meaning: text.min(1),
  pos: text,
  ipa: text,
  cefr: z.enum(["A1", "A2", "B1", "B2"]),
  kind: z.enum(["word", "phrase"]),
  collocations: z.array(text).max(100),
  commonError: text,
  tags: z.array(text).max(100),
  deckIds: z.array(key).max(100),
  notes: text,
  favorite: z.boolean(),
  difficult: z.boolean(),
  active: z.boolean(),
  deleted: z.boolean(),
  source: z.enum(["personal", "starter", "import"]),
  createdAt: iso,
  exampleIds: z.array(key).optional(),
});
export type WordSense = z.infer<typeof wordSchema>;
export const exampleSchema = z.object({
  id: key,
  wordId: key,
  en: text.min(1),
  tr: text.min(1),
  spans: z
    .array(z.object({ start: z.int().nonnegative(), end: z.int().positive() }))
    .min(1)
    .max(20),
  createdAt: iso,
});
export type ExampleSentence = z.infer<typeof exampleSchema>;
export const cardSchema = z.object({
  id: key,
  wordId: key,
  direction: z.enum(directions),
  createdAt: iso,
});
export type Flashcard = z.infer<typeof cardSchema>;
export const memorySchema = z.object({
  stability: z.number().finite().nonnegative(),
  stabilityFast: z.number().finite().nonnegative(),
  difficulty: z.number().min(0).max(10),
  state: z.int().min(0).max(3),
  scheduleStatus: z.enum(["new", "learning", "review"]),
  dueAt: iso,
  lastReviewAt: iso.nullable(),
  cardId: key,
  reps: z.int().nonnegative(),
  lapses: z.int().nonnegative(),
  scheduledDays: z.number().finite().nonnegative(),
  learningStep: z.int().nonnegative(),
});
export type Memory = z.infer<typeof memorySchema>;
export const stateSchema = z.object({
  id: key,
  cardId: key,
  memory: memorySchema,
  version: z.int().nonnegative(),
  model: z.literal("FSRS-7"),
});
export type ReviewState = z.infer<typeof stateSchema>;
export const eventSchema = z.object({
  id: key,
  wordId: key,
  cardId: key,
  exampleId: key,
  sessionId: key,
  at: iso,
  timezone: key,
  localDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  mode: z.enum(modes),
  direction: z.enum(directions),
  answer: text,
  prompt: text.optional(),
  expected: text.optional(),
  wordAtReview: text.optional(),
  meaningAtReview: text.optional(),
  assessment: z.enum(["automatic", "self"]),
  correct: z.boolean().nullable(),
  prediction: z.enum(["remembered", "unsure", "forgot", "unrecorded"]),
  rating: z.int().min(1).max(4),
  effectiveRating: z.int().min(1).max(4),
  hint: z.boolean(),
  translation: z.boolean(),
  answerMs: z.number().finite().min(0).max(3600000),
  activeMs: z.number().finite().min(0).max(3600000),
  previousDue: iso,
  nextDue: iso,
  difficult: z.boolean(),
  before: stateSchema,
  after: stateSchema,
  revlog: z.record(z.string(), z.unknown()),
  kind: z.enum(["review", "undo"]),
  undoOf: key.optional(),
});
export type ReviewEvent = Readonly<z.infer<typeof eventSchema>>;
export const sessionSchema = z.object({
  id: key,
  startedAt: iso,
  endedAt: iso.nullable(),
  mode: z.enum(modes),
  queue: z.array(key),
  newLimit: z.int().nonnegative(),
});
export type StudySession = z.infer<typeof sessionSchema>;
export const deckSchema = z.object({
  id: key,
  name: text.min(1),
  description: text,
});
export type Deck = z.infer<typeof deckSchema>;
export const settingsSchema = z.object({
  id: z.literal("settings"),
  dailyNew: z.int().min(1).max(100),
  dailyMinutes: z.int().min(1).max(180),
  retention: z.number().min(0.8).max(0.97),
  mode: z.enum(modes),
  speechRate: z.number().min(0.5).max(1.25),
  voice: z.enum(["en-US", "en-GB"]),
  theme: z.enum(["light", "dark", "system"]),
  motion: z.boolean(),
  difficultFirst: z.boolean(),
  autoNext: z.boolean(),
  showTranslation: z.boolean(),
  collocationExplanations: z.boolean(),
  collocationTests: z.boolean(),
  lastBackup: iso.nullable(),
  onboarded: z.boolean(),
});
export type UserSettings = z.infer<typeof settingsSchema>;
export const defaults: UserSettings = {
  id: "settings",
  dailyNew: 10,
  dailyMinutes: 10,
  retention: 0.9,
  mode: "understand",
  speechRate: 0.85,
  voice: "en-US",
  theme: "system",
  motion: true,
  difficultFirst: true,
  autoNext: true,
  showTranslation: false,
  collocationExplanations: true,
  collocationTests: false,
  lastBackup: null,
  onboarded: false,
};
export interface Snapshot {
  words: WordSense[];
  examples: ExampleSentence[];
  cards: Flashcard[];
  states: ReviewState[];
  events: ReviewEvent[];
  sessions: StudySession[];
  decks: Deck[];
  settings: UserSettings;
}
export function examplesFor(s: Snapshot, wordId: string) {
  const w = s.words.find((w) => w.id === wordId);
  return s.examples.filter(
    (e) => e.wordId === wordId && (!w?.exampleIds || w.exampleIds.includes(e.id)),
  );
}
export function validExample(ex: ExampleSentence) {
  let previous = 0;
  for (const s of ex.spans) {
    if (
      s.start < previous ||
      s.end <= s.start ||
      s.end > ex.en.length ||
      !ex.en.slice(s.start, s.end).trim()
    )
      return false;
    previous = s.end;
  }
  return ex.spans.length > 0;
}
export function parseMarked(marked: string): {
  en: string;
  spans: ExampleSentence["spans"];
} {
  let en = "";
  const spans: ExampleSentence["spans"] = [];
  let i = 0;
  while (i < marked.length) {
    if (marked[i] === "[") {
      const end = marked.indexOf("]", i + 1);
      if (end < 0 || marked.slice(i + 1, end).includes("["))
        throw new Error("Hedef kelimeyi [köşeli parantez] içinde belirt.");
      const target = marked.slice(i + 1, end);
      if (!target.trim()) throw new Error("Hedef kelime boş olamaz.");
      spans.push({ start: en.length, end: en.length + target.length });
      en += target;
      i = end + 1;
    } else {
      if (marked[i] === "]") throw new Error("Köşeli parantezleri kontrol et.");
      en += marked[i++];
    }
  }
  if (!spans.length)
    throw new Error("Cümlede hedef kelimeyi [köşeli parantez] içine al.");
  return { en, spans };
}
export function markExample(ex: ExampleSentence) {
  let out = "",
    i = 0;
  for (const s of ex.spans) {
    out += ex.en.slice(i, s.start) + "[" + ex.en.slice(s.start, s.end) + "]";
    i = s.end;
  }
  return out + ex.en.slice(i);
}
export function target(ex: ExampleSentence) {
  return ex.spans.map((s) => ex.en.slice(s.start, s.end)).join(" ");
}
export function cloze(ex: ExampleSentence) {
  let out = "",
    i = 0;
  for (const s of ex.spans) {
    out += ex.en.slice(i, s.start) + "_____";
    i = s.end;
  }
  return out + ex.en.slice(i);
}
// Keep apostrophes/hyphens: dont != don't, re-sign != resign. Only terminal punctuation is cosmetic.
export function collocationExercise(lemma: string, chunk: string) {
  const normalized = lemma.toLocaleLowerCase("en-US");
  const tokens = [...chunk.matchAll(/[\p{L}]+(?:['’][\p{L}]+)*/gu)];
  const ignored = new Set([
    "a",
    "an",
    "the",
    "to",
    "of",
    "in",
    "by",
    "for",
    "on",
    "at",
    "with",
  ]);
  const token = tokens.find(
    (t) =>
      t[0].toLocaleLowerCase("en-US") !== normalized &&
      !ignored.has(t[0].toLocaleLowerCase("en-US")),
  );
  if (token?.index !== undefined)
    return {
      prompt:
        chunk.slice(0, token.index) +
        "_____" +
        chunk.slice(token.index + token[0].length),
      expected: token[0],
    };
  return { prompt: "_____", expected: chunk };
}
export function normalizeAnswer(s: string) {
  return s
    .normalize("NFKC")
    .replace(/[’‘]/g, "'")
    .trim()
    .toLocaleLowerCase("en-US")
    .replace(/[.!?,;:]+$/u, "")
    .trim()
    .replace(/\s+/g, " ");
}
export function wordKey(w: Pick<WordSense, "lemma" | "sense" | "pos">) {
  return [
    normalizeAnswer(w.lemma),
    normalizeAnswer(w.sense),
    normalizeAnswer(w.pos),
  ].join("|");
}
export function lemmaKey(w: WordSense) {
  return normalizeAnswer(w.lemma);
}
export function deviceZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Istanbul";
}
const dayFormatters = new Map<string, Intl.DateTimeFormat>();
export function dayKey(date: Date | string, zone = deviceZone()) {
  if (!dayFormatters.has(zone))
    dayFormatters.set(
      zone,
      new Intl.DateTimeFormat("en-CA", {
        timeZone: zone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    );
  const parts = dayFormatters.get(zone)!.formatToParts(new Date(date));
  return ["year", "month", "day"]
    .map((t) => parts.find((p) => p.type === t)!.value)
    .join("-");
}
export function shiftDay(day: string, n: number) {
  const d = new Date(day + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
export function dateLabel(s: string | null) {
  return s
    ? new Intl.DateTimeFormat("tr-TR", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(s))
    : "Henüz çalışılmadı";
}
export const ratingLabels = ["", "Tekrar", "Zorlandım", "Bildim", "Çok kolay"];
export function durationLabel(ms: number) {
  if (ms <= 0) return "0 sn";
  if (ms < 1000) return "<1 sn";
  if (ms < 60000) return Math.round(ms / 1000) + " sn";
  return (
    new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(ms / 60000) +
    " dk"
  );
}
export function activeReviews(events: readonly ReviewEvent[]) {
  const undone = new Set(events.filter((e) => e.kind === "undo").map((e) => e.undoOf));
  return events.filter((e) => e.kind === "review" && !undone.has(e.id));
}
